import { useState } from 'react';
import { AIMWindow } from './AIMWindow';
import { AIMButton } from './AIMButton';
import { AIMInput } from './AIMInput';
import { RunningManIcon } from './RunningManIcon';
import { useLoginActions } from '@/hooks/useLoginActions';
import { Loader2 } from 'lucide-react';

export function SignOnScreen() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { loginWithExtension, loginWithNsec, generateKeys } = useLoginActions();

  const handleExtensionLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await loginWithExtension();
    } catch (err) {
      setError('Failed to connect to browser extension');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewAccount = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await generateKeys();
    } catch (err) {
      setError('Failed to create new account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen aim-signon-bg flex items-center justify-center p-4">
      <div className="flex flex-col items-center">
        {/* AIM Logo Area */}
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <RunningManIcon className="w-16 h-16 text-yellow-400 drop-shadow-lg" animate />
            <div>
              <h1 className="text-4xl font-bold text-white drop-shadow-lg tracking-tight">
                Buddy Chat
              </h1>
              <p className="text-sm text-white/80 drop-shadow">
                Nostr Instant Messenger
              </p>
            </div>
          </div>
        </div>

        {/* Sign On Window */}
        <AIMWindow
          title="Sign On"
          icon={<RunningManIcon className="w-4 h-4" />}
          className="w-[320px]"
          showMinimize={false}
        >
          <div className="p-4 space-y-4">
            {/* Running Man Animation */}
            <div className="flex justify-center py-2">
              <RunningManIcon className="w-20 h-20" animate={isLoading} />
            </div>

            {/* Welcome Text */}
            <div className="text-center text-sm">
              <p className="font-bold mb-1">Welcome to Buddy Chat!</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Sign on using your Nostr identity
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-red-600 text-xs text-center bg-red-100 dark:bg-red-900/30 p-2 border border-red-300 dark:border-red-800">
                {error}
              </div>
            )}

            {/* Sign On Options */}
            <div className="space-y-3">
              {/* Extension Login */}
              <AIMButton
                variant="primary"
                onClick={handleExtensionLogin}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                Sign On with Extension
              </AIMButton>

              {/* Divider */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                <span className="text-[10px] text-[hsl(var(--muted-foreground))]">or</span>
                <div className="flex-1 h-px bg-[hsl(var(--border))]" />
              </div>

              {/* New Account */}
              <AIMButton
                onClick={handleNewAccount}
                disabled={isLoading}
                className="w-full"
              >
                Get a Screen Name
              </AIMButton>
            </div>

            {/* Footer Links */}
            <div className="text-center text-[10px] text-[hsl(var(--muted-foreground))] space-x-2 pt-2">
              <a href="https://shakespeare.diy" className="hover:underline" target="_blank" rel="noopener noreferrer">
                Vibed with Shakespeare
              </a>
              <span>|</span>
              <a href="https://nostr.com" className="hover:underline" target="_blank" rel="noopener noreferrer">
                About Nostr
              </a>
            </div>
          </div>
        </AIMWindow>

        {/* Version Info */}
        <div className="mt-4 text-center text-[10px] text-white/60">
          <p>Version 1.0.0 (Nostr Protocol)</p>
          <p className="mt-1">2026 Buddy Chat. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
